import { useEffect, useMemo } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import type { LinkField } from "@prismicio/client";
import { CustomContentPageDocument } from "prismicio-types";
import { asText } from "@prismicio/helpers";

/**
 * Schema.org JSON-LD types used in Wovar
 */
export type SchemaType =
  | "WebSite"
  | "Organization"
  | "ContactPage"
  | "AboutPage"
  | "CollectionPage"
  | "WebPage"
  | "BreadcrumbList"
  | "ItemList"
  | "VideoObject";

export interface BreadcrumbItem {
  id: string;
  name: string;
  path: string;
}

export interface ProductItem {
  name: string;
  url: string;
  image?: string;
  price?: number;
  priceCurrency?: string;
}

export interface ProductListItem {
  name: string;
  slug: string;
  image?: string;
  price?: number;
  priceCurrency?: string;
}

/**
 * Minimal product shape for ItemList schema (see {@link buildItemListSchema}).
 */
export interface RawProductForItemList {
  name: string;
  slug: string;
  defaultImage?: { url?: string } | null;
  images?: Array<{ urlThumbnail?: string }> | null;
  salePriceInclTax?: { value?: number; currencyCode?: string } | null;
  priceInclTax?: { value?: number; currencyCode?: string } | null;
}

/**
 * Hook to inject JSON-LD schema into the document
 * @param schemaId - Unique identifier for the schema script (to prevent duplicates)
 * @param schema - The schema object to inject (null removes existing script)
 */
export function useJsonLdSchema(
  schemaId: string,
  schema: object | object[] | null
) {
  const isHydrated = useHydrated();

  useEffect(() => {
    const scriptSelector = `script[data-schema-id="${schemaId}"]`;
    const existingScript = document.querySelector(scriptSelector);

    if (!isHydrated || !schema) {
      if (existingScript) {
        existingScript.remove();
      }
      return;
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-schema-id", schemaId);
    script.innerHTML = JSON.stringify(schema);

    if (existingScript) {
      existingScript.replaceWith(script);
    } else {
      document.head.appendChild(script);
    }

    return () => {
      const scriptToRemove = document.querySelector(scriptSelector);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [isHydrated, schemaId, schema]);
}

// Extract base URL (protocol + host) from a full URL
export function getBaseUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return "";
  }
}

// Helper to extract URL from Prismic LinkField
export const getLinkUrl = (link: LinkField): string | null => {
  if (!link || link.link_type === "Any") return null;
  if ("url" in link && link.url) return link.url;
  return null;
};

// Extract phone number from contact text (e.g. "050-211-3434" or "+31857735133")
export const extractPhoneNumber = (text: string): string | null => {
  const phoneMatch = text.match(/[+]?[\d][\d\s\-()]{6,}/);
  return phoneMatch ? phoneMatch[0].trim() : null;
};


/**
 * Hook for WebSite schema (home page)
 */
export function useWebSiteSchema(params: {
  name: string;
  canonicalsUrl: string;
}) {
  const { name, canonicalsUrl } = params;
  const baseUrl = useMemo(() => getBaseUrl(canonicalsUrl), [canonicalsUrl]);
  const schema = useMemo(
    () => buildWebSiteSchema({ name, url: baseUrl }),
    [name, baseUrl]
  );
  useJsonLdSchema("website-schema", schema);
}

/**
 * Hook for ContactPage schema
 */
export function useContactPageSchema(params: {
  name: string;
  canonicalsUrl: string;
  description?: string;
}) {
  const { name, canonicalsUrl, description } = params;
  const schema = useMemo(
    () => buildContactPageSchema({ name, url: canonicalsUrl, description }),
    [name, canonicalsUrl, description]
  );
  useJsonLdSchema("contact-page-schema", schema);
}

/**
 * Hook for ReviewPage schema (WebPage with AggregateRating)
 */
export function useReviewPageSchema(
  params: {
    name: string;
    url: string;
    description?: string;
    ratingValue?: string;
    reviewCount?: string;
  } | null
) {
  const schema = useMemo(() => {
    if (!params) return null;
    return buildReviewPageSchema({
      name: params.name,
      url: params.url,
      description: params.description,
      aggregateRating: params.ratingValue
        ? {
            ratingValue: params.ratingValue,
            bestRating: "5",
            worstRating: "1",
            reviewCount: params.reviewCount || "0",
          }
        : undefined,
    });
  }, [params]);
  useJsonLdSchema("review-page-schema", schema);
}

/**
 * Hook for VideoObject schema. Pass a unique schemaId per video (e.g. video-schema-${videoID}). Pass null to skip.
 */
export function useVideoSchema(
  schemaId: string,
  params: {
    name: string;
    embedUrl?: string;
    contentUrl?: string;
    thumbnailUrl?: string;
    description?: string;
    uploadDate?: string;
  } | null
) {
  const schema = useMemo(
    () => (params ? buildVideoObjectSchema(params) : null),
    [params]
  );
  useJsonLdSchema(schemaId, schema);
}

/**
 * Build VideoObject schema (schema.org)
 */
export function buildVideoObjectSchema(params: {
  name: string;
  embedUrl?: string;
  contentUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  uploadDate?: string;
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: params.name,
  };
  if (params.embedUrl) schema.embedUrl = params.embedUrl;
  if (params.contentUrl) schema.contentUrl = params.contentUrl;
  if (params.thumbnailUrl) schema.thumbnailUrl = params.thumbnailUrl;
  if (params.description) schema.description = params.description;
  if (params.uploadDate) schema.uploadDate = params.uploadDate;
  return schema;
}

/**
 * Build WebSite schema
 */
export function buildWebSiteSchema(params: {
  name: string;
  url: string;
  searchUrl?: string;
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: params.name,
    url: params.url,
  };

  if (params.searchUrl) {
    schema.potentialAction = {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${params.searchUrl}?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    };
  }

  return schema;
}

/**
 * Build Organization schema
 */
export function buildOrganizationSchema(params: {
  name: string;
  url: string;
  alternateName?: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  founders?: Array<{ name: string }>;
  email?: string;
  telephone?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  areaServed?: string[];
  contactPoints?: Array<{
    telephone: string;
    email: string;
    contactType: string;
    areaServed: string;
    availableLanguage: string;
  }>;
  aggregateRating?: {
    ratingValue: string;
    bestRating: string;
    worstRating: string;
    reviewCount: string;
  };
  returnPolicy?: {
    applicableCountry: string[];
    returnPolicyCountry?: string;
    returnPolicyUrl?: string;
    returnPolicyCategory?: string;
    merchantReturnDays?: number;
    returnMethod?: string;
    returnFees?: string;
    refundType?: string;
  };
  sameAs?: string[];
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: params.name,
    url: params.url,
  };

  if (params.alternateName) schema.alternateName = params.alternateName;
  if (params.logo) schema.logo = params.logo;
  if (params.description) schema.description = params.description;
  if (params.foundingDate) schema.foundingDate = params.foundingDate;
  if (params.email) schema.email = params.email;
  if (params.telephone) schema.telephone = params.telephone;
  if (params.sameAs) schema.sameAs = params.sameAs;
  if (params.areaServed) schema.areaServed = params.areaServed;

  if (params.founders && params.founders.length > 0) {
    schema.founders = params.founders.map((founder) => ({
      "@type": "Person",
      name: founder.name,
    }));
  }

  if (params.address) {
    schema.address = {
      "@type": "PostalAddress",
      ...params.address,
    };
  }

  if (params.contactPoints && params.contactPoints.length > 0) {
    schema.contactPoint = params.contactPoints.map((cp) => ({
      "@type": "ContactPoint",
      telephone: cp.telephone,
      email: cp.email,
      contactType: cp.contactType,
      areaServed: cp.areaServed,
      availableLanguage: cp.availableLanguage,
    }));
  }

  if (params.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: params.aggregateRating.ratingValue,
      bestRating: params.aggregateRating.bestRating,
      worstRating: params.aggregateRating.worstRating,
      reviewCount: params.aggregateRating.reviewCount,
    };
  }

  if (params.returnPolicy) {
    const rp: Record<string, unknown> = {
      "@type": "MerchantReturnPolicy",
      applicableCountry: params.returnPolicy.applicableCountry,
    };
    if (params.returnPolicy.returnPolicyCountry) {
      rp.returnPolicyCountry = params.returnPolicy.returnPolicyCountry;
    }
    if (params.returnPolicy.returnPolicyUrl) {
      rp.url = params.returnPolicy.returnPolicyUrl;
    }
    if (params.returnPolicy.returnPolicyCategory) {
      rp.returnPolicyCategory = params.returnPolicy.returnPolicyCategory;
    }
    if (params.returnPolicy.merchantReturnDays != null) {
      rp.merchantReturnDays = params.returnPolicy.merchantReturnDays;
    }
    if (params.returnPolicy.returnMethod) {
      rp.returnMethod = params.returnPolicy.returnMethod;
    }
    if (params.returnPolicy.returnFees) {
      rp.returnFees = params.returnPolicy.returnFees;
    }
    if (params.returnPolicy.refundType) {
      rp.refundType = params.returnPolicy.refundType;
    }
    schema.hasMerchantReturnPolicy = rp;
  }

  return schema;
}

/**
 * Build ContactPage schema
 */
export function buildContactPageSchema(params: {
  name: string;
  url: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: params.name,
    url: params.url,
    ...(params.description && { description: params.description }),
  };
}

/**
 * Build AboutPage schema
 */
export function buildAboutPageSchema(params: {
  name: string;
  url: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: params.name,
    url: params.url,
    ...(params.description && { description: params.description }),
  };
}

/**
 * Build CollectionPage schema
 */
export function buildCollectionPageSchema(params: {
  name: string;
  url: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: params.name,
    url: params.url,
    ...(params.description && { description: params.description }),
  };
}

/**
 * Build WebPage schema
 */
export function buildWebPageSchema(params: {
  name: string;
  url: string;
  description?: string;
  inLanguage?: string;
  isPartOf?: { name: string; url: string };
  datePublished?: string;
  dateModified?: string;
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: params.name,
    url: params.url,
  };

  if (params.description) schema.description = params.description;
  if (params.inLanguage) schema.inLanguage = params.inLanguage;
  if (params.datePublished) schema.datePublished = params.datePublished;
  if (params.dateModified) schema.dateModified = params.dateModified;

  if (params.isPartOf) {
    schema.isPartOf = {
      "@type": "WebSite",
      name: params.isPartOf.name,
      url: params.isPartOf.url,
    };
  }

  return schema;
}

/**
 * Build ReviewPage schema (WebPage with AggregateRating)
 */
export function buildReviewPageSchema(params: {
  name: string;
  url: string;
  description?: string;
  aggregateRating?: {
    ratingValue: string;
    bestRating: string;
    worstRating: string;
    reviewCount: string;
  };
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: params.name,
    url: params.url,
    ...(params.description && { description: params.description }),
  };

  if (params.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: params.aggregateRating.ratingValue,
      bestRating: params.aggregateRating.bestRating,
      worstRating: params.aggregateRating.worstRating,
      reviewCount: params.aggregateRating.reviewCount,
    };
  }

  return schema;
}

/**
 * Build BreadcrumbList schema
 */
export function buildBreadcrumbListSchema(
  breadcrumbs: BreadcrumbItem[],
  baseUrl: string
) {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: toPlainText(item.name, 200),
      item: `${baseUrl}${item.path.startsWith("/") ? "" : "/"}${item.path}`,
    })),
  };
}

/**
 * Build BreadcrumbList schema from full page URL (uses origin for item URLs).
 */
export function buildBreadcrumbSchema(params: {
  breadcrumbs: BreadcrumbItem[];
  canonicalsUrl: string;
}) {
  const baseUrl = getBaseUrl(params.canonicalsUrl);
  return buildBreadcrumbListSchema(params.breadcrumbs, baseUrl);
}

/**
 * Build ItemList schema for a category (or similar) product listing from API-shaped products.
 */
export function buildItemListSchema(params: {
  products: RawProductForItemList[];
  canonicalsUrl: string;
  listName?: string;
  maxItems?: number;
}): Record<string, unknown> | null {
  const { products, canonicalsUrl, listName, maxItems = 20 } = params;
  const baseUrl = getBaseUrl(canonicalsUrl);
  if (!products?.length) return null;

  const items: ProductItem[] = products.slice(0, maxItems).map((p) => ({
    name: p.name,
    url: `${baseUrl}/${p.slug}`,
    image: p.defaultImage?.url || p.images?.[0]?.urlThumbnail,
    price: p.salePriceInclTax?.value ?? p.priceInclTax?.value,
    priceCurrency:
      p.salePriceInclTax?.currencyCode ||
      p.priceInclTax?.currencyCode ||
      "EUR",
  }));

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...(listName && { name: listName }),
    numberOfItems: items.length,
    itemListElement: items.map((product, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Product",
        name: product.name,
        url: product.url,
        ...(product.image && { image: product.image }),
        ...(product.price && {
          offers: {
            "@type": "Offer",
            price: product.price,
            priceCurrency: product.priceCurrency || "EUR",
          },
        }),
      },
    })),
  };
}

/**
 * Minimal product shape for Product schema (see {@link buildProductSchema}).
 */
export interface RawProductForSchema {
  name?: string;
  sku?: string;
  description?: string;
  gtin?: string;
  seo?: { metaDescription?: string } | null;
  defaultImage?: { urlOriginal?: string } | null;
  images?: Array<{ urlOriginal?: string }> | null;
  categories?: Array<{ name?: string }> | null;
  priceInclTax?: { value?: number; currencyCode?: string } | null;
  salePriceInclTax?: { value?: number; currencyCode?: string } | null;
  unitsOfMeasure?: Array<{
    primary?: boolean | string;
    availability?: { canAddToCart?: boolean } | null;
  }> | null;
}

const HANDLING_TIME_DAYS = { minValue: 0, maxValue: 1 };

const SHIPPING_POLICY: Record<
  string,
  {
    transitMin: number;
    transitMax: number;
    slug: string;
    standardRate: number;
    currency: string;
    freeFrom?: number;
  }
> = {
  NL: { transitMin: 1, transitMax: 1, slug: "verzending-levering", standardRate: 7.95, freeFrom: 150, currency: "EUR" },
  BE: { transitMin: 1, transitMax: 1, slug: "verzending-levering", standardRate: 8.95, freeFrom: 150, currency: "EUR" },
  DE: { transitMin: 1, transitMax: 2, slug: "versand-und-lieferung", standardRate: 8.99, freeFrom: 150, currency: "EUR" },
  FR: { transitMin: 1, transitMax: 2, slug: "expedition-et-livraison", standardRate: 12.99, freeFrom: 150, currency: "EUR" },
  GB: { transitMin: 2, transitMax: 3, slug: "delivery", standardRate: 12.2, freeFrom: 170, currency: "GBP" },
  IE: { transitMin: 2, transitMax: 3, slug: "delivery", standardRate: 14.99, currency: "EUR" },
  DK: { transitMin: 2, transitMax: 3, slug: "levering", standardRate: 81.91, freeFrom: 1490, currency: "DKK" },
  SE: { transitMin: 2, transitMax: 3, slug: "leverans", standardRate: 229.57, freeFrom: 2240, currency: "SEK" },
  PL: { transitMin: 2, transitMax: 3, slug: "dostawa", standardRate: 57.82, freeFrom: 860, currency: "PLN" },
  PT: { transitMin: 2, transitMax: 3, slug: "entrega", standardRate: 10.99, freeFrom: 200, currency: "EUR" },
  IT: { transitMin: 2, transitMax: 3, slug: "spedizione-consegna", standardRate: 11.99, freeFrom: 200, currency: "EUR" },
  ES: { transitMin: 2, transitMax: 3, slug: "envios-entregas", standardRate: 10.99, freeFrom: 200, currency: "EUR" },
};

/** True when this slug is the shipping page for the visitor's country. */
export function isShippingPolicyPage(slug: string, locale?: string): boolean {
  const policy = SHIPPING_POLICY[getCountryFromLocale(locale)];
  return !!policy && slug.replace(/^\/|\/$/g, "").toLowerCase() === policy.slug;
}

/**
 * Organization-level ShippingService: the standard policy Google applies to
 * every product, replacing the per-product shippingDetails we used to emit.
 */
export function buildShippingServiceSchema(params: {
  canonicalsUrl: string;
  locale?: string;
}): Record<string, unknown> | null {
  const country = getCountryFromLocale(params.locale);
  const policy = SHIPPING_POLICY[country];
  if (!policy?.slug || policy.standardRate == null || !policy.currency) return null;
  const { currency, freeFrom } = policy;

  const transitTime = {
    "@type": "ServicePeriod",
    duration: {
      "@type": "QuantitativeValue",
      minValue: policy.transitMin,
      maxValue: policy.transitMax,
      unitCode: "DAY",
    },
  };

  const orderValue = (bounds: Record<string, number>) => ({
    "@type": "MonetaryAmount",
    valueAddedTaxIncluded: true,
    currency,
    ...bounds,
  });
  const band = (value: number, bounds?: Record<string, number>) => ({
    "@type": "ShippingConditions",
    shippingDestination: { "@type": "DefinedRegion", addressCountry: country },
    ...(bounds ? { orderValue: orderValue(bounds) } : {}),
    shippingRate: { "@type": "MonetaryAmount", value, currency },
    transitTime,
  });

  const shippingConditions =
    freeFrom == null
      ? [band(policy.standardRate)]
      : [
          band(policy.standardRate, { minValue: 0, maxValue: freeFrom - 0.01 }),
          band(0, { minValue: freeFrom }),
        ];

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Wovar",
    url: getBaseUrl(params.canonicalsUrl),
    hasShippingService: {
      "@type": "ShippingService",
      "@id": "#standard-shipping",
      fulfillmentType: "https://schema.org/FulfillmentTypeDelivery",
      handlingTime: {
        "@type": "ServicePeriod",
        duration: {
          "@type": "QuantitativeValue",
          ...HANDLING_TIME_DAYS,
          unitCode: "DAY",
        },
      },
      shippingConditions,
    },
  };
}

/** Sitewide return terms — keep in sync with buildOrganizationSchemaFromParams. */
export const MERCHANT_RETURN_POLICY = {
  returnPolicyCountry: "NL",
  merchantReturnDays: 30,
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
  refundType: "https://schema.org/FullRefund",
};

/**
 * The LOCALE env is "lang-country" (e.g. "nl-be"), so the country is the second segment.
 */
export function getCountryFromLocale(locale?: string | null): string {
  const country = locale?.split("-")[1];
  return country ? country.toUpperCase() : "NL";
}

const MARKETS_WITHOUT_STOREFRONT: Record<string, string[]> = { DE: ["AT"] };

/** The countries one storefront sells to: its own, plus any with no domain. */
function getMarketCountries(locale?: string | null): string[] {
  const country = getCountryFromLocale(locale);
  return [country, ...(MARKETS_WITHOUT_STOREFRONT[country] ?? [])];
}

const HTML_ENTITIES: Record<string, string> = {
  nbsp: " ", lt: "<", gt: ">", quot: '"', apos: "'",
  euro: "€", copy: "©", reg: "®", trade: "™", deg: "°",
  times: "×", minus: "−", plusmn: "±", sup2: "²", sup3: "³",
  frac12: "½", frac14: "¼", bull: "•", middot: "·", hellip: "…",
  mdash: "—", ndash: "–", laquo: "«", raquo: "»",
  lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”",
  eacute: "é", egrave: "è", ecirc: "ê", agrave: "à", acirc: "â",
  ccedil: "ç", ugrave: "ù", ucirc: "û", uuml: "ü", ouml: "ö",
  auml: "ä", iuml: "ï", euml: "ë", szlig: "ß", ntilde: "ñ",
  aacute: "á", iacute: "í", oacute: "ó", uacute: "ú",
};

const MAX_CODE_POINT = 0x10ffff;

// Product descriptions come from Akeneo as HTML; schema.org wants plain text.
export function toPlainText(html?: string | null, maxLength = 500): string {
  const text = String(html ?? "")
    .replace(/<[^>]*>/g, " ")
    // &amp; resolves last so "&amp;nbsp;" doesn't decode twice.
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+\d*);/gi, (match, entity: string) => {
      if (entity.toLowerCase() === "amp") return match;
      if (entity.startsWith("#")) {
        const code = entity[1]?.toLowerCase() === "x"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
        // fromCodePoint throws above U+10FFFF and on lone surrogates.
        const valid =
          Number.isInteger(code) &&
          code >= 0 &&
          code <= MAX_CODE_POINT &&
          !(code >= 0xd800 && code <= 0xdfff);
        return valid ? String.fromCodePoint(code) : match;
      }
      return HTML_ENTITIES[entity.toLowerCase()] ?? match;
    })
    .replace(/&amp;/gi, "&")
    // Remix serialises the JSON-LD straight into a <script> with no escaping,
    // so a decoded "</script>" from Akeneo copy would close the block early.
    .replace(/<\/|<!--/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
}

// Google flags a malformed GTIN as an error, so drop anything that fails the check digit.
export function normalizeGtin(raw?: string | null): string | null {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (![8, 12, 13, 14].includes(digits.length)) return null;

  const body = digits.slice(0, -1);
  let sum = 0;
  for (let i = body.length - 1, weight = 3; i >= 0; i--) {
    sum += Number(body[i]) * weight;
    weight = weight === 3 ? 1 : 3;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === Number(digits[digits.length - 1]) ? digits : null;
}

/**
 * Build Product schema for a PDP, including the offer's shipping and return terms.
 */
export function buildProductSchema(params: {
  product: RawProductForSchema | null;
  canonicalsUrl: string;
  reviewAggregate?: { ratingValue: number; reviewCount: number } | null;
}): Record<string, unknown> | null {
  const {
    product,
    canonicalsUrl,
    reviewAggregate,
  } = params;
  if (!product?.name) return null;

  const currency =
    product.salePriceInclTax?.currencyCode ||
    product.priceInclTax?.currencyCode ||
    "EUR";
  const price = product.salePriceInclTax?.value ?? product.priceInclTax?.value;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: toPlainText(product.name, 200),
    url: canonicalsUrl,
    brand: { "@type": "Brand", name: "Wovar" },
  };

  // Full-size images only — thumbnails are below Google's 1200px minimum.
  const images = [
    ...new Set(
      [
        product.defaultImage?.urlOriginal,
        ...(product.images ?? []).map((image) => image?.urlOriginal),
      ].filter((url): url is string => Boolean(url))
    ),
  ];
  if (images.length) schema.image = images;

  const description =
    toPlainText(product.seo?.metaDescription) || toPlainText(product.description);
  if (description) schema.description = description;
  if (product.sku) schema.sku = product.sku;

  // gtin13 is the typed property for 13-digit EANs; generic gtin covers 8/12/14.
  const gtin = normalizeGtin(product.gtin);
  if (gtin) {
    if (gtin.length === 13) schema.gtin13 = gtin;
    else schema.gtin = gtin;
  }

  const category = toPlainText(
    product.categories?.[product.categories.length - 1]?.name,
    200
  );
  if (category) schema.category = category;

  // A 0 price means ERP pricing is missing, not a free product — omit the offer.
  if (price != null && price > 0) {
    // Same primary-UoM the PDP renders availability from.
    const primaryUom =
      product.unitsOfMeasure?.find((uom) => uom.primary) ??
      product.unitsOfMeasure?.[0];

    schema.offers = {
      "@type": "Offer",
      url: canonicalsUrl,
      price,
      priceCurrency: currency,
      itemCondition: "https://schema.org/NewCondition",
      availability:
        primaryUom?.availability?.canAddToCart === true
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: "Wovar" },
    };
  }

  // Yotpo defaults a missing average_score to 0, which is below worstRating.
  if (reviewAggregate && reviewAggregate.reviewCount > 0 && reviewAggregate.ratingValue > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(reviewAggregate.ratingValue),
      bestRating: "5",
      worstRating: "1",
      reviewCount: String(reviewAggregate.reviewCount),
    };
  }

  return schema;
}

/**
 * Minimal slice shape for FAQ extraction (Prismic-agnostic).
 * Use with extractFAQFromSlices so FAQ parsing lives in one place.
 */
export interface SliceWithFaqItems {
  slice_type: string;
  items?: Array<{ question: unknown; answer: unknown }>;
}

/**
 * Extract FAQ question/answer pairs from an array of slices (e.g. Prismic slices).
 * Centralizes FAQ parsing so FaqSection and WvCustomContentPage don't duplicate logic.
 */
export function extractFAQFromSlices(
  slices: SliceWithFaqItems[] | null | undefined,
  getText: (field: unknown) => string
): Array<{ question: string; answer: string }> {
  if (!slices?.length) return [];
  return slices.flatMap((slice) => {
    if (slice.slice_type !== "faq_slice" || !Array.isArray(slice.items))
      return [];
    return slice.items
      .map((item) => ({
        question: getText(item.question) || "",
        answer: getText(item.answer) || "",
      }))
      .filter((q) => q.question && q.answer);
  });
}

/**
 * Build FAQPage schema
 */
export function buildFAQPageSchema(
  questions: Array<{ question: string; answer: string }>
) {
  if (!questions || questions.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/**
 * Extract FAQ items from Prismic slices and build FAQPage schema in one step.
 * Use this in loaders instead of calling extractFAQFromSlices + buildFAQPageSchema separately.
 */
export function buildFAQSchemaFromSlices(
  slices: unknown[] | null | undefined
): Record<string, unknown> | null {
  return buildFAQPageSchema(
    extractFAQFromSlices(
      slices as SliceWithFaqItems[] | undefined,
      (field) => asText(field as never)
    )
  );
}

/**
 * Pure (server-safe) equivalent of useOrganizationSchema.
 * Accepts the same params but performs the mapping without React hooks.
 */
export function buildOrganizationSchemaFromParams(params: {
  canonicalsUrl: string;
  description: string;
  logoUrl: string;
  socialMedias: Array<{ link: LinkField }>;
  languages: Array<{ domain: string; country: string; locale: string }>;
  locale: string;
  contactText: string;
  headerRating?: { rating: number; reviewCount: number } | null;
}): Record<string, unknown> {
  const {
    canonicalsUrl,
    description,
    logoUrl,
    socialMedias,
    languages,
    locale,
    contactText,
    headerRating,
  } = params;

  const baseUrl = getBaseUrl(canonicalsUrl);

  const socialMediaUrls: string[] = socialMedias?.length
    ? socialMedias.map((s) => getLinkUrl(s.link)).filter((u): u is string => u !== null)
    : [];

  const domainUrls: string[] = languages?.length
    ? languages.map((l) => `https://www.${l.domain}`)
    : [];

  // parseLocaleEnv mislabels its fields: `country` actually holds the language
  // ("nl-be" -> country:"nl"). Derive the real country from the locale string.
  const areaServed: string[] = languages?.length
    ? [...new Set(languages.map((l) => getCountryFromLocale(l.locale)))]
    : [];

  const currentLang = languages?.find((l) => l.locale === locale);
  const telephone = currentLang ? extractPhoneNumber(contactText) : null;
  const [lang] = locale.split("-");
  const contactPoints =
    telephone && currentLang
      ? [
          {
            telephone,
            email: `info@${currentLang.domain}`,
            contactType: "customer service",
            areaServed: getCountryFromLocale(currentLang.locale),
            availableLanguage: lang ?? "",
          },
        ]
      : [];

  return buildOrganizationSchema({
    name: "Wovar",
    alternateName: "Wovar B.V.",
    url: baseUrl,
    logo: logoUrl,
    description,
    foundingDate: "2017",
    founders: [{ name: "Jesse Keizer" }],
    address: {
      streetAddress: "Manchesterweg 55",
      addressLocality: "Groningen",
      addressRegion: "GR",
      postalCode: "9744 TS",
      addressCountry: "NL",
    },
    areaServed: areaServed.length > 0 ? areaServed : undefined,
    contactPoints: contactPoints.length > 0 ? contactPoints : undefined,
    aggregateRating: {
      ratingValue: (headerRating?.rating ?? 4.68).toFixed(1),
      bestRating: "5",
      worstRating: "1",
      reviewCount: String(headerRating?.reviewCount ?? 8724),
    },
    returnPolicy: {
      ...MERCHANT_RETURN_POLICY,
      applicableCountry: getMarketCountries(locale),
    },
    sameAs:
      socialMediaUrls.length > 0 || domainUrls.length > 0
        ? [...socialMediaUrls, ...domainUrls]
        : undefined,
  });
}

export type CustomContentJsonLd = {
  webPage: Record<string, unknown> | null;
  breadcrumb: Record<string, unknown> | null;
  faq: Record<string, unknown> | null;
};

function getBreadcrumbItemsFromCustomContentSlices(
  slices: unknown[] | undefined
): BreadcrumbItem[] {
  const breadcrumbSlice = slices?.find(
    (slice) => (slice as { slice_type?: string }).slice_type === "breadcrumbs"
  );
  if (
    typeof breadcrumbSlice !== "object" ||
    breadcrumbSlice === null ||
    !("items" in breadcrumbSlice)
  ) {
    return [];
  }

  const items = (breadcrumbSlice as { items: Array<{ text: unknown; link: unknown }> })
    .items;
  if (!items?.length) return [];

  return [
    { id: "home", name: "Home", path: "/" },
    ...items.map((item) => ({
      id: asText(item.text as never) || "",
      name: asText(item.text as never) || "",
      path: asText(item.link as never) || "",
    })),
  ].filter((b) => b.name);
}

/**
 * JSON-LD for custom content pages (WebPage, BreadcrumbList, FAQPage).
 * Matches client hook behavior: WebPage is omitted when the page has FAQ content.
 */
export function buildCustomContentPageJsonLd(params: {
  customContentPage: CustomContentPageDocument;
  canonicalsUrl: string;
  locale: string;
}): CustomContentJsonLd {
  const { customContentPage, canonicalsUrl, locale } = params;
  const slices = customContentPage?.data?.slices as unknown[] | undefined;

  const faqQuestions = extractFAQFromSlices(
    slices as SliceWithFaqItems[] | undefined,
    (field) => asText(field as never)
  );
  const faq = buildFAQPageSchema(faqQuestions);

  const baseUrl = getBaseUrl(canonicalsUrl);
  const breadcrumbItems = getBreadcrumbItemsFromCustomContentSlices(slices);
  const breadcrumb = buildBreadcrumbListSchema(breadcrumbItems, baseUrl);

  let webPage: Record<string, unknown> | null = null;
  const data = customContentPage?.data as Record<string, unknown> | undefined;
  const pageTitle =
    asText(
      (data?.seo_title ??
        data?.metaTitle ??
        data?.page_title) as never
    ) || undefined;
  const pageDescription =
    asText(
      (data?.seo_description ?? data?.metaDescription) as never
    ) || undefined;

  if (pageTitle) {
    webPage = buildWebPageSchema({
      name: pageTitle,
      url: canonicalsUrl,
      description: pageDescription,
      inLanguage: locale?.split("-")[0] || undefined,
      isPartOf: baseUrl ? { name: "Wovar", url: baseUrl } : undefined,
      datePublished: customContentPage?.first_publication_date || undefined,
      dateModified: customContentPage?.last_publication_date || undefined,
    });
  }

  return { webPage, breadcrumb, faq };
}
